import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HEALTH_URL = `${BACKEND_URL}/health`;
const CHECK_TIMEOUT = 5000;   // 5s to decide if server is asleep
const POLL_INTERVAL = 4000;   // poll every 4s while waking

async function pingServer() {
  try {
    await axios.get(HEALTH_URL, { timeout: CHECK_TIMEOUT });
    return true;
  } catch {
    return false;
  }
}

export default function ServerStatus() {
  // null = checking, true = online, false = offline
  const [status, setStatus] = useState(null);
  const [waking, setWaking] = useState(false);
  const [dots, setDots] = useState("");
  const pollRef = useRef(null);
  const dotsRef = useRef(null);

  const stopPolling = () => {
    clearInterval(pollRef.current);
    clearInterval(dotsRef.current);
  };

  const startPolling = () => {
    setWaking(true);
    dotsRef.current = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 500);

    pollRef.current = setInterval(async () => {
      const alive = await pingServer();
      if (alive) {
        stopPolling();
        setStatus(true);
        setWaking(false);
      }
    }, POLL_INTERVAL);
  };

  useEffect(() => {
    let cancelled = false;
    pingServer().then((alive) => {
      if (!cancelled) setStatus(alive ? true : false);
    });
    return () => { cancelled = true; stopPolling(); };
  }, []);

  // Server online or still on initial check — render nothing
  if (status === null || status === true) return null;

  return (
    <div className="w-full mb-6 border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        {/* Animated power icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-3 h-3 rounded-full ${waking ? "bg-amber-400 animate-pulse" : "bg-red-400"}`} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            {waking ? `Server is waking up${dots}` : "Server is currently offline"}
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            {waking
              ? "Free-tier servers sleep after inactivity. This takes ~30–60 seconds."
              : "Running on a free-tier server that sleeps when idle. Wake it up before logging in."}
          </p>
        </div>

        {!waking && (
          <button
            onClick={startPolling}
            className="flex-shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-amber-400 hover:bg-amber-500 text-amber-900 transition-colors"
          >
            Wake Server
          </button>
        )}

        {waking && (
          <div className="flex-shrink-0 flex gap-1 items-center mt-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        )}
      </div>

      {waking && (
        <div className="mt-3 h-1 bg-amber-200 overflow-hidden">
          <div className="h-full bg-amber-400 animate-[progress_2s_ease-in-out_infinite]" style={{ width: "60%" }} />
        </div>
      )}
    </div>
  );
}
